import React from 'react';
import { Button, Drawer, Table } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import classnames from 'classnames';
import useApiClient from '../../../../hooks/api/useApiClient';
import { useGetOnboardingStatsQuery } from '../../../../store/api-slices';
import { downloadTableAsCsv } from '../../../../services/helpers/TableExportHelpers';

export function OnboardingQualityScore({ score }) {
  if (!score) {
    return 'N/A';
  }

  return (
    <span
      className={classnames({
        'text-brand-primary': score >= 8.6,
        'text-orange-600': score < 8.6 && score >= 5.7,
      })}
    >
      {score}
    </span>
  );
}

function DrawerDetails({ metric, dateRangeFilter, onClose, ...props }) {
  const { t } = useTranslation(null, { keyPrefix: 'Views:Dashboard:ServiceProvider:OnboardingTeam.details' });
  const { StatsManager } = useApiClient();
  const { data, isLoading } = useGetOnboardingStatsQuery(
    {
      apiManager: StatsManager,
      metric,
      ...dateRangeFilter,
    },
    { skip: !metric },
  );

  const formatLiveFrom = (item) => (item?.live_from ? dayjs(item.live_from).format('YYYY-MM-DD') : '-');
  const columns = [
    {
      title: t('name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('owner'),
      key: 'owner',
      dataIndex: 'owner_name',
    },
    {
      title: t('owner_phone'),
      key: 'owner_phone',
      dataIndex: 'owner_phone_number',
    },
    {
      title: t('owner_email'),
      key: 'owner_email',
      dataIndex: 'owner_email',
    },
    {
      title: t('live_from'),
      key: 'live_from',
      render: formatLiveFrom,
      csvRender: formatLiveFrom,
    },
    {
      title: t('onboarding_manager'),
      key: 'onboarding_manager',
      dataIndex: 'onboarding_manager',
    },
    {
      title: t('sales_manager'),
      key: 'sales_manager',
      dataIndex: 'sales_manager',
    },
    {
      title: t('onboarding_quality_score'),
      key: 'onboarding_quality_score',
      render: (item) => <OnboardingQualityScore score={item.onboarding_quality_score} />,
      csvRender: (item) => item.onboarding_quality_score ?? 'N/A',
    },
  ];

  return (
    <Drawer size="70dvw" {...props} open={!!metric} onClose={onClose}>
      <div className="mb-md flex justify-end">
        <Button
          icon={<ExportOutlined />}
          disabled={!data?.length}
          onClick={() => {
            const start = dateRangeFilter?.start_date ?? '';
            const end = dateRangeFilter?.end_date ?? '';
            downloadTableAsCsv({
              columns,
              dataSource: data,
              filename: `onboarding-${metric}-${start}-${end}.csv`,
            });
          }}
        >
          {t('export_csv')}
        </Button>
      </div>
      <Table loading={isLoading} dataSource={data} columns={columns} />
    </Drawer>
  );
}

export default DrawerDetails;
